import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { FuelType } from '@/types/price-comparison.types';
import { ProvinceTableResponse } from '@/types/pricing-province.types';

import {
  useGetFiltersQuery,
  useGetProvinceDataQuery,
  useGetGlobalSummaryQuery,
  useExportSummaryDataMutation,
  useExportProvinceDataMutation
} from '../../../services';

import { useSnackbar, useRolePermissions } from '../../../hooks';
import { useAppSelector } from '../../../store';
import { PerformanceStatusIndicator, ErrorState, LoadingTable, BrandOrderDialog } from '../../../components';

import { TableControls, SummaryTable, RegionalTable, RegionalTableHeader, PriceType } from '../components';

import './PricingProvincePage.css';

function PricingProvincePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { setSnackbarMessage } = useSnackbar();
  const { canCreate, canExport, canViewSales } = useRolePermissions();
  const isAuthorized = useAppSelector(state => state.global.isAuthorized);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const brandOrderLabel = isMobile ? 'Бренди (рег.)' : 'Налаштувати бренди (регіональні)';
  const [brandsDialogOpen, setBrandsDialogOpen] = useState(false);
  const [brandsProvinceDialogOpen, setBrandsProvinceDialogOpen] = useState(false);
  const [hideDiscounts, setHideDiscounts] = useState(false);
  const [priceTypes, setpriceTypes] = useState<PriceType>(PriceType.MODE);
  const [collapsedProvince, setCollapsedProvince] = useState<Record<string, boolean>>({});

  const {
    data: brands,
    isLoading: brandsLoading,
    isSuccess: brandsSuccess,
    error: brandsError,
    refetch: refetchBrands
  } = useGetFiltersQuery(undefined, { skip: !isAuthorized, refetchOnMountOrArgChange: true });

  const {
    data: { summaryData = [], summaryFuelTypes = [], performanceStatus } = {},
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    isSuccess: summarySuccess,
    error: summaryError,
    refetch: refetchSummary
  } = useGetGlobalSummaryQuery(
    { includeDiscounts: !hideDiscounts },
    { refetchOnMountOrArgChange: true, skip: !isAuthorized }
  );

  const {
    data: { provinceData = [], provinceFuelTypes = [], provincePerformanceStatus } = {},
    isLoading: provinceLoading,
    isSuccess: provinceSuccess,
    error: provinceError,
    refetch: refetchProvince
  } = useGetProvinceDataQuery(
    { includeDiscounts: !hideDiscounts },
    { refetchOnMountOrArgChange: true, skip: !isAuthorized }
  );

  const [exportSummaryData] = useExportSummaryDataMutation();
  const [exportProvinceData] = useExportProvinceDataMutation();

  const isAuthError = (err: any) => err?.status === 401 || err?.status === 403;
  const isError =
    (brandsError || summaryError || provinceError) &&
    !isAuthError(brandsError) &&
    !isAuthError(summaryError) &&
    !isAuthError(provinceError);
  const isLoading = brandsLoading || summaryLoading || provinceLoading;
  const isDataLoaded = brandsSuccess || summarySuccess || provinceSuccess;

  const availableBrands = brands?.brands || [];
  const apiFuelTypes = summaryFuelTypes.length > 0 ? summaryFuelTypes : provinceFuelTypes;
  const currentPerformanceStatus = performanceStatus || provincePerformanceStatus;

  const fuelTypes = apiFuelTypes.map((fuel: FuelType): string => fuel.fuel_type);

  useEffect(() => {
    if (isError) {
      setSnackbarMessage('Помилка завантаження даних', 'error');
    }
  }, [isError, setSnackbarMessage]);

  const formatPrice = (price?: number | null): string =>
    typeof price === 'number' && !isNaN(price) ? price.toFixed(2) : '';

  const handelePriceTypes = (event: ChangeEvent<HTMLInputElement>) => {
    setpriceTypes(event.target.value as PriceType);
  }

  const refetchData = () => {
    refetchSummary();
    refetchProvince();
  };

  const retryLoad = () => {
    refetchBrands();
    refetchData();
  };

  const exportSummaryPrices = async () => {
    await exportSummaryData(!hideDiscounts)
      .unwrap()
      .then(() => setSnackbarMessage('Файл успішно вивантажено', 'success'))
      .catch(() => setSnackbarMessage('Помилка вивантаження файлу', 'error'));
  };

  const exportProvincePrices = async (province: string) => {
    await exportProvinceData({ province })
      .unwrap()
      .then(() => setSnackbarMessage(`Файл для регіону ${province} успішно вивантажено`, 'success'))
      .catch(() => setSnackbarMessage('Помилка вивантаження файлу', 'error'));
  };

  const handleBrandDialogOpen = (state: boolean) => () => {
    setBrandsDialogOpen(state);
  };

  const handleBrandDialogProvinceOpen = (state: boolean) => () => {
    setBrandsProvinceDialogOpen(state);
  };

  const isProvinceCollapsed = (province: string) => !!collapsedProvince[province];

  const toggleProvince = (province: string) => () => {
    setCollapsedProvince(prev => ({
      ...prev,
      [province]: !prev[province]
    }));
  };

  const handleExportProvince = (province: string) => () => {
    exportProvincePrices(province);
  };

  const handleViewDetails = (province: string) => () => navigate(`/pricing-province/province/${province}`);

  if (isError) {
    return (
      <div className="pricing-container">
        <ErrorState message="Помилка завантаження динаміки цін" onRetry={retryLoad} />
      </div>
    );
  }

  return (
    <div className={`pricing-container ${isMobile ? 'mobile' : ''}`}>
      {isDataLoaded && (
        <PerformanceStatusIndicator performanceStatus={currentPerformanceStatus} onRefreshNeeded={retryLoad} />
      )}
      {isLoading ? (
        <LoadingTable message="Завантаження цін..." rows={10} columns={15} />
      ) : (
        <>
          {/* Summary Table */}
          <div className="summary-table-container">
            <h2 className="table-title">Узагальнена таблиця</h2>
            <TableControls
              isMobile={isMobile}
              hideDiscounts={hideDiscounts}
              showBrandOrder={canCreate}
              showExport={canExport}
              showHideDiscounts={canExport}
              onHideDiscountsChange={setHideDiscounts}
              onChangePriceTypes={handelePriceTypes}
              priceTypes={priceTypes}
              onExport={exportSummaryPrices}
              onBrandOrder={handleBrandDialogOpen(true)}
            />
            <SummaryTable
              summaryData={summaryData}
              fuelTypes={fuelTypes}
              priceType = {priceTypes}
              hideDiscounts={hideDiscounts}
              formatPrice={formatPrice}
              isMobile={isMobile}
              summaryFetching={summaryFetching}
              hideSales={!canViewSales}
            />
          </div>

          {/* Province Tables */}
          <div className="regional-tables-container">
            <TableControls
              title="Роздрібні ціни по області"
              onBrandOrder={handleBrandDialogProvinceOpen(true)}
              // priceTypes={priceTypes}
              // onChangePriceTypes={handelePriceTypes}
              showExport={false}
              showBrandOrder={canCreate}
              showHideDiscounts={false}
              isMobile={isMobile}
              brandOrderLabel={brandOrderLabel}
            />

            {provinceData.map((provinceTableData: ProvinceTableResponse) => {
              const province = provinceTableData.province;
              return (
                <div key={`province-${province}`} className="regional-table-container">
                  <RegionalTableHeader
                    title={province}
                    isMobile={isMobile}
                    isCollapsed={isProvinceCollapsed(province)}
                    onToggle={toggleProvince(province)}
                    onExport={handleExportProvince(province)}
                    onViewDetails={handleViewDetails(province)}
                    showExport={canExport}
                    showViewDetails={canExport}
                  />

                  {!isProvinceCollapsed(province) && (
                    <RegionalTable
                      regionKey={province}
                      brands={provinceTableData.brands}
                      fuelTypes={fuelTypes}
                      priceType={priceTypes}
                      formatPrice={formatPrice}
                      isMobile={isMobile}
                      lastAuthor={provinceTableData.lastAuthor}
                      lastUploadDatetime={provinceTableData.lastUploadDatetime}
                      hideSales={!canViewSales}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <BrandOrderDialog
        scope="general"
        context="province"
        open={brandsDialogOpen}
        availableBrands={availableBrands}
        onClose={handleBrandDialogOpen(false)}
      />
      <BrandOrderDialog
        scope="regional"
        context="province"
        open={brandsProvinceDialogOpen}
        availableBrands={availableBrands}
        onClose={handleBrandDialogProvinceOpen(false)}
      />
    </div>
  );
}

export default PricingProvincePage;
